import { SlidersHorizontal } from "lucide-react";
import { useGetList, useListContext, useTranslate } from "ra-core";
import { useEffect, useMemo, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

import { ChoiceChips } from "../design/ChoiceChips";
import {
  INVENTORY_ORIGINS,
  PROPERTY_OPTION_STATUSES,
  type InventoryItem,
  type PropertyType,
  type Sale,
} from "../types";
import {
  INVENTORY_DEFAULT_FILTERS,
  ORIGIN_FILTER_KEY,
  RENTA_OPERATION,
  AMK_CHOICE,
  buildPropertyTypeChoices,
  countActiveInventoryFilters,
  readFilterText,
  readOrigins,
  setInventoryFilter,
  type InventoryFilterValues,
} from "./opcionRules";

const SELECT_CLASS =
  "h-11 w-full rounded-md border bg-background px-3 text-base";

/** Text or numeric input that keeps its own draft so typing is never reset by the list's debounce. */
const DraftFilter = ({
  label,
  value,
  onCommit,
  numeric = false,
  className,
}: {
  label: string;
  value: string;
  onCommit: (text: string) => void;
  numeric?: boolean;
  className?: string;
}) => {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  return (
    <div className={cn("flex flex-col gap-1", className)}>
      <Label>{label}</Label>
      <Input
        type={numeric ? "number" : "search"}
        min={numeric ? 0 : undefined}
        inputMode={numeric ? "decimal" : undefined}
        className={cn("h-11", numeric && "tabular-nums")}
        aria-label={label}
        value={draft}
        onChange={(event) => {
          setDraft(event.target.value);
          onCommit(event.target.value);
        }}
      />
    </div>
  );
};

const SelectFilter = ({
  label,
  value,
  choices,
  onChange,
}: {
  label: string;
  value: string;
  choices: { id: string; name: string }[];
  onChange: (value: string) => void;
}) => (
  <div className="flex flex-col gap-1">
    <Label>{label}</Label>
    <select
      aria-label={label}
      className={SELECT_CLASS}
      value={value}
      onChange={(event) => onChange(event.target.value)}
    >
      <option value="">{label}</option>
      {choices.map((choice) => (
        <option key={choice.id} value={choice.id}>
          {choice.name}
        </option>
      ))}
    </select>
  </div>
);

/** Advisors with a clave plus AMK, for the responsable filter. */
const useResponsableChoices = () => {
  const { data } = useGetList<Sale>("sales", {
    pagination: { page: 1, perPage: 500 },
    sort: { field: "advisor_code", order: "ASC" },
  });
  return useMemo(
    () => [
      { id: AMK_CHOICE, name: "AMK" },
      ...(data ?? [])
        .filter((sale) => (sale.advisor_code ?? "").trim() !== "")
        .map((sale) => ({
          id: String(sale.id),
          name: (sale.advisor_code ?? "").trim(),
        })),
    ],
    [data],
  );
};

/** Zones already used by the inventory (a free-text column). */
const useZoneChoices = () => {
  const { data } = useGetList<InventoryItem>("inventory_items", {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: "zone", order: "ASC" },
  });
  return useMemo(() => {
    const zones = new Set<string>();
    (data ?? []).forEach((item) => {
      if (item.zone) zones.add(item.zone);
    });
    return Array.from(zones).map((zone) => ({ id: zone, name: zone }));
  }, [data]);
};

/**
 * The catalogue types by code, and the choices for the filter: the active types plus any type
 * used by a row of the current page.
 */
export const usePropertyTypes = (usedCodes: readonly string[] = []) => {
  const { data } = useGetList<PropertyType>("property_types", {
    pagination: { page: 1, perPage: 200 },
    sort: { field: "position", order: "ASC" },
  });
  return useMemo(
    () => ({
      labels: new Map((data ?? []).map((type) => [type.code, type.label])),
      choices: buildPropertyTypeChoices(data ?? [], usedCodes),
    }),
    [data, usedCodes],
  );
};

/**
 * Almikia Epic EC2d (TASK-137): filters of the Inventario Almikia board. The state lives in the
 * list's filter values in the query shape (see `inventoryFilterRules.ts`); every change is
 * server-side. On mobile everything but the search sits in a collapsible panel.
 */
export const InventoryFilters = () => {
  const translate = useTranslate();
  const { filterValues, setFilters, data } = useListContext<InventoryItem>();
  const [open, setOpen] = useState(false);
  const zoneChoices = useZoneChoices();
  const responsableChoices = useResponsableChoices();
  const usedCodes = useMemo(
    () => Array.from(new Set((data ?? []).map((item) => item.property_type))),
    [data],
  );
  const { choices: typeChoices } = usePropertyTypes(usedCodes);

  // Cumulative state so two quick changes (one debounced) never overwrite each other.
  const latest = useRef<InventoryFilterValues>(filterValues);
  useEffect(() => {
    latest.current = filterValues;
  }, [filterValues]);
  const change = (
    key: string,
    value: string | readonly string[] | undefined,
    debounce = false,
  ) => {
    latest.current = setInventoryFilter(latest.current, key, value);
    setFilters(latest.current, {}, debounce);
  };

  const operation = readFilterText(filterValues, "operation");
  const furnished = readFilterText(filterValues, "furnished");
  const activeCount = countActiveInventoryFilters(filterValues);
  const t = (key: string) => translate(`crm.opciones.filters.${key}`);

  return (
    <div className="mb-3 flex flex-col gap-3" data-testid="inventory-filters">
      <div className="flex items-end gap-2">
        <DraftFilter
          className="min-w-0 flex-1"
          label={translate("crm.opciones.search")}
          value={readFilterText(filterValues, "q")}
          onCommit={(text) => change("q", text, true)}
        />
        <Button
          type="button"
          variant="outline"
          className="min-h-11 md:hidden"
          aria-expanded={open}
          aria-controls="inventory-filter-panel"
          onClick={() => setOpen(!open)}
        >
          <SlidersHorizontal className="size-4" aria-hidden="true" />
          {t("panel")}
          {activeCount > 0 ? (
            <span className="tabular-nums">({activeCount})</span>
          ) : null}
        </Button>
      </div>
      <div
        id="inventory-filter-panel"
        className={cn("flex-col gap-4 md:flex", open ? "flex" : "hidden")}
      >
        <div className="flex flex-wrap gap-x-6 gap-y-3">
          <ChoiceChips
            label={t("origin")}
            mode="multi"
            value={readOrigins(filterValues)}
            options={INVENTORY_ORIGINS.map((origin) => ({
              id: origin,
              label: translate(`crm.opciones.origin.${origin}`),
            }))}
            onChange={(next) => change(ORIGIN_FILTER_KEY, next)}
          />
          <ChoiceChips
            label={t("operation")}
            mode="single"
            value={operation === "" ? [] : [operation]}
            options={["venta", "renta"].map((id) => ({
              id,
              label: translate(`crm.opciones.operation.${id}`),
            }))}
            onChange={(next) => change("operation", next[0])}
          />
          {operation === RENTA_OPERATION ? (
            <ChoiceChips
              label={t("furnished")}
              mode="single"
              value={furnished === "" ? [] : [furnished]}
              options={["amueblado", "sin_amueblar"].map((id) => ({
                id,
                label: translate(`crm.opciones.furnished.${id}`),
              }))}
              onChange={(next) => change("furnished", next[0])}
            />
          ) : null}
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <SelectFilter
            label={t("status")}
            value={readFilterText(filterValues, "status")}
            choices={PROPERTY_OPTION_STATUSES.map((status) => ({
              id: status,
              name: translate(`crm.opciones.status.${status}`),
            }))}
            onChange={(value) => change("status", value)}
          />
          <SelectFilter
            label={t("type")}
            value={readFilterText(filterValues, "property_type")}
            choices={typeChoices}
            onChange={(value) => change("property_type", value)}
          />
          <SelectFilter
            label={t("zone")}
            value={readFilterText(filterValues, "zone")}
            choices={zoneChoices}
            onChange={(value) => change("zone", value)}
          />
          <SelectFilter
            label={t("responsable")}
            value={readFilterText(filterValues, "responsable")}
            choices={responsableChoices}
            onChange={(value) => change("responsable", value)}
          />
          {(
            [
              ["price@gte", "price_min"],
              ["price@lte", "price_max"],
              ["area_m2@gte", "area_min"],
              ["area_m2@lte", "area_max"],
              ["levels@gte", "levels_min"],
              ["bedrooms@gte", "bedrooms_min"],
              ["bathrooms@gte", "bathrooms_min"],
              ["parking@gte", "parking_min"],
            ] as const
          ).map(([key, labelKey]) => (
            <DraftFilter
              key={key}
              numeric
              label={t(labelKey)}
              value={readFilterText(filterValues, key)}
              onCommit={(text) => change(key, text, true)}
            />
          ))}
        </div>
        {activeCount > 0 ? (
          <div>
            <Button
              type="button"
              variant="outline"
              className="min-h-11"
              onClick={() => {
                latest.current = INVENTORY_DEFAULT_FILTERS;
                setFilters(INVENTORY_DEFAULT_FILTERS, {}, false);
              }}
            >
              {t("clear")}
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
};
